import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { safeCapture } from "../analytics";
import { getEvents } from "../analytics/events";
import { widgetApi } from "../services/api";
import {
  shouldShowFirstSendWarning,
  type FirstSendNotice,
} from "../utils/firstSendWarning";

const inflight = new Map<string, Promise<FirstSendNotice | null>>();
let shownThisSession = false;

export function wasFirstSendWarningShown(): boolean {
  return shownThisSession;
}

export function clearFirstSendWarningCache(): void {
  inflight.clear();
  shownThisSession = false;
}

async function loadNotice(
  linkId: string,
  locale: string
): Promise<FirstSendNotice | null> {
  const key = `${linkId}:${locale}`;
  const pending = inflight.get(key);
  if (pending) return pending;
  const request = widgetApi
    .get(`/widget/links/${linkId}/notices/first-send-warning`, {
      params: { locale },
    })
    .then((response) => (response.data?.data ?? null) as FirstSendNotice | null)
    .catch(() => null);
  inflight.set(key, request);
  return request;
}

export function useFirstSendWarning(input: {
  linkId?: string;
  sendAmount: number | null;
  sourceCurrency?: string | null;
  fundingMethod?: string | null;
  /** Family B flows must pass false until the payer is bound. */
  enabled?: boolean;
}) {
  const { i18n } = useTranslation();
  const [notice, setNotice] = useState<FirstSendNotice | null>(null);
  const active = input.enabled !== false && Boolean(input.linkId);

  useEffect(() => {
    if (!active || !input.linkId) {
      setNotice(null);
      return;
    }
    let cancelled = false;
    loadNotice(input.linkId, i18n.language).then((next) => {
      if (!cancelled) setNotice(next);
    });
    return () => {
      cancelled = true;
    };
  }, [active, input.linkId, i18n.language]);

  const visible = shouldShowFirstSendWarning({
    notice,
    sendAmount: input.sendAmount,
    sourceCurrency: input.sourceCurrency,
    fundingMethod: input.fundingMethod,
  });

  useEffect(() => {
    if (!visible || shownThisSession) return;
    shownThisSession = true;
    safeCapture(getEvents().FIRST_SEND_WARNING_SHOWN, {
      threshold_amount: notice?.thresholdAmount,
      currency: notice?.currency,
      funding_method: input.fundingMethod,
    });
  }, [visible, notice, input.fundingMethod]);

  return {
    visible,
    title: notice?.title ?? "",
    body: notice?.body ?? "",
  };
}
